import { createElement } from 'react';
import { createRoute, type AnyRoute } from '@tanstack/react-router';
import { ArtigoEditorPage } from '@/features/knowledge/ArtigoEditorPage';
import { ArtigoPage } from '@/features/knowledge/ArtigoPage';
import { ConhecimentoPage } from '@/features/knowledge/ConhecimentoPage';
import {
  CAMINHO_ARTIGO,
  CAMINHO_ARTIGO_EDITAR,
  CAMINHO_ARTIGO_NOVO,
  CAMINHO_ARTIGOS,
} from '@/features/knowledge/navegacao';

/**
 * Rotas da base de conhecimento.
 *
 * Exportadas como **fábrica**, e não como objetos prontos: `createRoute` exige
 * a rota pai, e o pai (`appRoute`, com o `AppShell` e a guarda de sessão) mora
 * em `app/router.tsx`, que pertence a outro dono nesta rodada. Receber o pai
 * por parâmetro deixa a integração ser uma linha lá, sem que esta feature
 * importe de dentro de `app/` — o que fecharia um ciclo entre o roteador e a
 * tela que ele monta.
 *
 * Quatro rotas irmãs, e não uma raiz com filhas: lista, ficha, criação e
 * edição não compartilham casca. A ficha é uma tela inteira, e o editor é
 * outra — com duas colunas e pré-visualização.
 *
 * ⚠ **Ordem importa.** `/conhecimento/novo` vem ANTES de `/conhecimento/$artigoId`,
 * senão "novo" é lido como identificador de artigo e a tela de criação nunca
 * abre. O TanStack ordena rotas estáticas antes das dinâmicas por conta
 * própria, mas depender disso é depender de detalhe de implementação — e o
 * custo de deixar explícito é zero.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  Integração esperada em `app/router.tsx` — DUAS linhas
 * ─────────────────────────────────────────────────────────────────────────
 *
 * ```ts
 * import { criarRotasDeConhecimento } from '@/features/knowledge/routes';
 *
 * const {
 *   lista: conhecimentoRoute,
 *   novo: artigoNovoRoute,
 *   detalhe: artigoRoute,
 *   editar: artigoEditarRoute,
 * } = criarRotasDeConhecimento(appRoute);
 *
 * appRoute.addChildren([
 *   …,
 *   conhecimentoRoute, artigoNovoRoute, artigoRoute, artigoEditarRoute,
 * ]);
 * ```
 *
 * E em `app/nav.ts`, o item de menu — entre "Erros conhecidos" e "Clientes",
 * porque é da mesma família (o que a Aptum **sabe**) e é de quem opera:
 *
 * ```ts
 * { to: '/conhecimento', label: 'Conhecimento', icon: BookOpen,
 *   hint: 'Artigos, procedimentos e o que já foi resolvido antes' }
 * ```
 *
 * O tipo `NavPath` de `nav.ts` precisa ganhar `'/conhecimento'`.
 */
export function criarRotasDeConhecimento<TRotaPai extends AnyRoute>(rotaPai: TRotaPai) {
  const lista = createRoute({
    getParentRoute: () => rotaPai,
    path: CAMINHO_ARTIGOS,
    /**
     * O termo de busca vive na URL, e não no estado da tela: assim o resultado
     * pode ser mandado por mensagem, e recarregar não perde a pesquisa.
     */
    validateSearch: (entrada: Record<string, unknown>): { q?: string } => {
      const q = entrada['q'];
      return typeof q === 'string' && q.trim() !== '' ? { q: q.trim() } : {};
    },
    component: ConhecimentoPage,
  });

  const novo = createRoute({
    getParentRoute: () => rotaPai,
    path: CAMINHO_ARTIGO_NOVO,
    /**
     * `?chamado=` chega do painel de sugestão dentro do chamado: é o caminho
     * "resolvi isto, vou escrever" — e é ele que fecha o laço do critério 8.5.
     */
    validateSearch: (entrada: Record<string, unknown>): { chamado?: string } => {
      const c = entrada['chamado'];
      return typeof c === 'string' && c.trim() !== '' ? { chamado: c.trim() } : {};
    },
    component: ArtigoEditorPage,
  });

  const detalhe = createRoute({
    getParentRoute: () => rotaPai,
    path: CAMINHO_ARTIGO,
    // `createElement` em vez de JSX para este arquivo continuar sendo `.ts`, e
    // a conversão do parâmetro porque o roteador só infere `{ artigoId }`
    // depois que a árvore estiver registrada em `app/router.tsx`.
    component: () => {
      const { artigoId } = detalhe.useParams() as { artigoId: string };
      return createElement(ArtigoPage, { artigoId });
    },
  });

  const editar = createRoute({
    getParentRoute: () => rotaPai,
    path: CAMINHO_ARTIGO_EDITAR,
    component: () => {
      const { artigoId } = editar.useParams() as { artigoId: string };
      return createElement(ArtigoEditorPage, { artigoId });
    },
  });

  return { lista, novo, detalhe, editar };
}

export { CAMINHO_ARTIGO, CAMINHO_ARTIGO_EDITAR, CAMINHO_ARTIGO_NOVO, CAMINHO_ARTIGOS };
